/**
 * Reporting an interaction once, and once per press.
 *
 * WHY A KEY AT ALL
 * ----------------
 * The beacon goes over a network that loses things. Without a name for the press, a browser that
 * resends would write a second row and the report would say a certificate was downloaded twice by
 * somebody who pressed once. The key names the INTENT: it is minted at the press and travels with
 * every retry of that press, and a genuinely separate press mints a new one. No time window is
 * involved, because «same kind, same minute» would silently merge a person who really did press
 * twice — which is the opposite of measuring.
 *
 * WHY NOTHING IS AWAITED
 * ----------------------
 * The download, the print window and the navigation are what the visitor asked for. A measurement
 * never stands in front of them, and a measurement that fails costs them nothing.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { PublicInteractionKind } from '../../api/publicApi';
import { useV07CommandsForMeasurement } from '../data/commands-context';

/** A name for one press. Opaque, per-tab, and carrying nothing about the person. */
function pressKey(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c?.randomUUID) return c.randomUUID();
  /* A browser without `randomUUID` still gets a distinct key per press. */
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export interface InteractionRecorder {
  /** Reports one press. Fire-and-forget by construction: it returns nothing to wait on. */
  note(kind: PublicInteractionKind): void;
  /** Reports an act that happens once per opened page, however many times React renders it. */
  noteOnce(kind: PublicInteractionKind): void;
}

export function useInteractionRecorder(code: string | null | undefined): InteractionRecorder {
  /*
   * Measurement never breaks a page. A surface mounted without a command provider — and a command
   * object that predates this measurement — simply records nothing, rather than throwing a render
   * over a report nobody is waiting for.
   */
  const commands = useV07CommandsForMeasurement();
  const once = useRef(new Set<string>());

  const note = useCallback(
    (kind: PublicInteractionKind): void => {
      if (!code || typeof commands?.recordInteraction !== 'function') return;
      void commands.recordInteraction({ code, kind, key: pressKey() });
    },
    [commands, code],
  );

  const noteOnce = useCallback(
    (kind: PublicInteractionKind): void => {
      if (!code || typeof commands?.recordInteraction !== 'function') return;
      const seen = `${code}:${kind}`;
      if (once.current.has(seen)) return;
      once.current.add(seen);
      /* One key for the whole visit, so a re-render or a retry cannot become a second view. */
      void commands.recordInteraction({ code, kind, key: `${kind}:${pressKey()}` });
    },
    [commands, code],
  );

  return { note, noteOnce };
}

/** The «this page was opened» report, run once per mounted record. */
export function usePageViewed(code: string | null | undefined, kind: PublicInteractionKind): void {
  const { noteOnce } = useInteractionRecorder(code);
  useEffect(() => {
    noteOnce(kind);
  }, [noteOnce, kind]);
}
